import { useLocalSearchParams, useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";

import {
  AU06_CURRENT_FY,
  annualRepayment,
  au06RulesFor,
  calculateAu06,
  explanationBullets,
  formatAud,
  repaymentHeadline,
} from "@aucash/rules/au06";

import { AppBar, Button, Card, Chip, Eyebrow } from "../../src/components/brand";
import { Checkbox, SliderInput } from "../../src/components/controls";
import { FreePdfCard } from "../../src/components/PdfCard";
import { SaveCard } from "../../src/components/SaveCard";
import { DisclaimerBlock } from "../../src/disclaimer";
import { useAppObserva } from "../../src/observa/use-app-observa";
import { trpc } from "../../src/trpc";
import { colors, fonts, radius, space, tabular, webNoOutline } from "../../src/theme";

type Au06Result = Awaited<ReturnType<typeof trpc.calc.getAu06Assessment.query>>;

/** Whole-percent label for a marginal/whole-income rate, e.g. "15%". */
function ratePct(fraction: number): string {
  return `${+(fraction * 100).toFixed(2)}%`;
}

export default function Au06Result(): JSX.Element {
  const router = useRouter();
  const params = useLocalSearchParams<{ id?: string | string[] }>();
  const id = Array.isArray(params.id) ? params.id[0] : params.id;

  const [result, setResult] = useState<Au06Result | null>(null);
  const [error, setError] = useState<string | null>(null);
  const observa = useAppObserva();

  useEffect(() => {
    if (id === undefined || id === "") return;
    let active = true;
    setResult(null);
    setError(null);
    trpc.calc.getAu06Assessment
      .query({ id })
      .then((res) => {
        if (!active) return;
        setResult(res);
        void observa?.event({
          eventType: "result.viewed",
          payload: { product: "AU06" },
        });
      })
      .catch((err: unknown) => {
        if (active) setError(err instanceof Error ? err.message : "Request failed");
      });
    return () => {
      active = false;
    };
  }, [id, observa]);

  if (id === undefined || id === "") {
    return (
      <View style={styles.root}>
        <StatusBar style="dark" />
        <View style={styles.centered}>
          <Text style={styles.errorTitle}>We couldn&apos;t find that result</Text>
          <Text style={styles.muted}>
            The link may be incomplete. Head back and enter your details again.
          </Text>
          <Button label="Back to calculator" onPress={() => router.replace("/au06")} />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.root}>
      <StatusBar style="dark" />
      <AppBar pill="HECS / HELP · result" onRestart={() => router.replace("/au06")} />
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.stage}>
          <Pressable onPress={() => router.replace("/au06")} accessibilityRole="button">
            <Text style={styles.backLink}>← Edit your details</Text>
          </Pressable>

          {/* §3.2: full disclaimer at the top, before any number. */}
          <DisclaimerBlock product="au06" />

          {error !== null ? (
            <Card>
              <Text style={styles.errorTitle}>Couldn&apos;t reach the calculator</Text>
              <Text style={styles.muted}>{error}</Text>
              <Text style={styles.muted}>
                If you&apos;re running this locally, start the API with{" "}
                <Text style={styles.mono}>bun run dev:api</Text> (:7501).
              </Text>
            </Card>
          ) : result === null ? (
            <View style={styles.loading}>
              <ActivityIndicator color={colors.honey} />
            </View>
          ) : (
            <ResultBody result={result} id={id} />
          )}
        </View>
      </ScrollView>
    </View>
  );
}

function ResultBody({ result, id }: { result: Au06Result; id: string }): JSX.Element {
  const { output, ruleVersion, sources, sourcesVerifiedAt } = result;
  const bullets = explanationBullets(output, ruleVersion, sourcesVerifiedAt);
  const due = output.repaymentDue;
  const heroColor = due ? colors.honeyDeep : colors.ocean;

  return (
    <>
      <View style={styles.resHead}>
        <Eyebrow>Your repayment · FY26</Eyebrow>
        <Chip
          label={due ? "● Repayment due" : "✓ Below the threshold"}
          tone={due ? "est" : "owed"}
        />
      </View>

      <Card style={{ gap: space.sm }}>
        <Text style={styles.lead}>{repaymentHeadline(output)}</Text>
        <Text style={[styles.heroBig, { color: heroColor }]}>{annualRepayment(output)}</Text>
        <View style={styles.split}>
          <View style={styles.cell}>
            <Text style={styles.cellKey}>This financial year</Text>
            <Text style={styles.cellVal}>{formatAud(output.compulsoryRepayment)}</Text>
          </View>
          {output.fractionOfBalance !== null ? (
            <View style={styles.cell}>
              <Text style={styles.cellKey}>Share of your balance</Text>
              <Text style={styles.cellVal}>{ratePct(output.fractionOfBalance)}</Text>
            </View>
          ) : (
            <View style={styles.cell}>
              <Text style={styles.cellKey}>Repayment band</Text>
              <Text style={styles.cellVal}>{output.thresholdBand}</Text>
            </View>
          )}
        </View>
      </Card>

      <Section title="Why these numbers">
        {bullets.map((b, i) => (
          <View key={i} style={styles.whyRow}>
            <View style={styles.whyIcon}>
              <Text style={styles.whyIconText}>i</Text>
            </View>
            <Text style={styles.whyText}>{b}</Text>
          </View>
        ))}
      </Section>

      {/* §6.3 what-if: drag your repayment income and watch the compulsory
          repayment recompute against the same FY26 ruleset, client-side off the
          bundled pure engine. Information only (validation §3). */}
      <WhatIf startIncome={output.repaymentIncome} />

      {/* Salary-packaging handoff (business-plan v2: AU-06's real adjacent line).
          Information only, not advice (validation §3): we explain the genuine
          mechanic — packaged amounts are added back into repayment income — and
          point at the ATO. A disclosed referral to a salary-packaging comparison
          lands here once a partner is signed (it's a non-financial-product
          service, so no AFSL question); until then this is purely informational. */}
      <Section title="One thing that can change this">
        <Card tone="sand">
          <Text style={styles.cardTitle}>Salary packaging affects your repayment</Text>
          <Text style={styles.muted}>
            If you salary-package — for example a novated car lease — the packaged
            amount is added back when the ATO works out your repayment income. So
            packaging can change what you repay. It's worth understanding how it
            applies to you before you decide anything; this is general information,
            not advice.
          </Text>
          <SourceLink
            label="How repayment income is worked out — ATO"
            url={sources.repaymentIncome}
          />
        </Card>
      </Section>

      <Section title="Where these numbers come from">
        <SourceLink label="Study & training loan repayment thresholds — ATO" url={sources.thresholds} />
        <SourceLink label="What's new: marginal repayment system — ATO" url={sources.whatsNew} />
        <SourceLink label="Repayment income (income tests) — ATO" url={sources.repaymentIncome} />
        <Text style={styles.verified}>
          Rules last verified {sourcesVerifiedAt} · ruleset {ruleVersion}
        </Text>
      </Section>

      <FreePdfCard id={id} product="au06" />

      <SaveCard id={id} />

      <Section title="Also worth checking">
        <CrossSellCard
          text="Private health cover? Check your Medicare Levy Surcharge and LHC loading →"
          to="/au08"
        />
        <CrossSellCard
          text="Kids in care? See your Child Care Subsidy and check a past year →"
          to="/au13"
        />
      </Section>

      <EmailCaptureCard />
    </>
  );
}

function WhatIf({ startIncome }: { startIncome: number }): JSX.Element {
  const rules = useMemo(() => au06RulesFor(AU06_CURRENT_FY), []);
  const [income, setIncome] = useState(startIncome);

  const max = Math.max(250_000, Math.ceil((startIncome * 1.5) / 10_000) * 10_000);

  const out = useMemo(
    () => calculateAu06({ repaymentIncome: income, assessmentYear: AU06_CURRENT_FY }, rules),
    [income, rules],
  );

  return (
    <Section title="What if your income changes?">
      <Card tone="sand" style={{ gap: space.md }}>
        <Text style={styles.cellKey}>Repayment income</Text>
        <Text style={styles.whatIfIncome}>{formatAud(income)}</Text>
        <SliderInput
          value={income}
          onChange={setIncome}
          min={0}
          max={max}
          step={1_000}
          maxLabel={`${formatAud(max)}+`}
          hideValue
          accessibilityLabel="Repayment income to model"
        />
        <View style={styles.whatIfGrid}>
          <View style={styles.whatIfStat}>
            <Text style={styles.cellKey}>Compulsory repayment</Text>
            <Text style={styles.whatIfStatValue}>{formatAud(out.compulsoryRepayment)}</Text>
          </View>
          <View style={styles.whatIfStat}>
            <Text style={styles.cellKey}>
              {out.repaymentDue ? "Rate on this income" : "Repayment band"}
            </Text>
            <Text style={styles.whatIfStatValue}>
              {out.repaymentDue ? ratePct(out.marginalRate) : "Below threshold"}
            </Text>
          </View>
        </View>
        <Text style={styles.whatIfBand}>Band: {out.thresholdBand}</Text>
        {out.nextBand !== null ? (
          <Text style={styles.muted}>
            The next band starts at {formatAud(out.nextBand.at)} —{" "}
            {formatAud(out.nextBand.away)} above this income.
          </Text>
        ) : null}
      </Card>
    </Section>
  );
}

// Post-result email capture — ACMA opt-in, information-only, stores only email.
function EmailCaptureCard(): JSX.Element {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (done) {
    return (
      <Card tone="sand">
        <Text style={styles.cardTitle}>You&apos;re on the list</Text>
        <Text style={styles.muted}>
          We&apos;ll email you if the thresholds change. Check your inbox for a
          welcome note — every email has a one-tap unsubscribe.
        </Text>
      </Card>
    );
  }

  const canSubmit = consent && email.trim().length > 0 && !submitting;

  const onSubmit = (): void => {
    if (!canSubmit) return;
    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError("Enter a valid email address.");
      return;
    }
    setSubmitting(true);
    setError(null);
    trpc.email.subscribe
      .mutate({ email: trimmed, consent: true, source: "au06-result" })
      .then(() => setDone(true))
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : "Couldn't sign you up."),
      )
      .finally(() => setSubmitting(false));
  };

  return (
    <Card tone="sand">
      <Text style={styles.cardTitle}>Keep these numbers current</Text>
      <Text style={styles.muted}>
        ATO thresholds and rates index every July. Get a short email when they
        do — no spam, just the update. We store only your email.
      </Text>
      <TextInput
        value={email}
        onChangeText={setEmail}
        autoCapitalize="none"
        autoCorrect={false}
        keyboardType="email-address"
        textContentType="emailAddress"
        placeholder="you@example.com"
        placeholderTextColor={colors.muted}
        style={styles.input}
        accessibilityLabel="Email address for updates"
      />
      <Checkbox
        checked={consent}
        onChange={setConsent}
        label="Email me occasional updates from Money Back Australia. I can unsubscribe any time."
      />
      <Button
        label={submitting ? "Signing you up…" : "Email me updates"}
        onPress={onSubmit}
        disabled={!canSubmit}
      />
      {error !== null ? <Text style={styles.muted}>{error}</Text> : null}
    </Card>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function SourceLink({ label, url }: { label: string; url: string | undefined }): JSX.Element | null {
  if (url === undefined) return null;
  return (
    <Pressable
      onPress={() => void Linking.openURL(url)}
      accessibilityRole="link"
      style={styles.sourceLink}
    >
      <Text style={styles.sourceLinkText}>{label}</Text>
    </Pressable>
  );
}

function CrossSellCard({ text, to }: { text: string; to: string }): JSX.Element {
  const router = useRouter();
  return (
    <Pressable onPress={() => router.push(to)} accessibilityRole="button" style={styles.crossSell}>
      <Text style={styles.crossSellText}>{text}</Text>
      <View style={styles.pill}>
        <Text style={styles.pillText}>Calculate →</Text>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.cream },
  scroll: { padding: space.xl, paddingBottom: space.xxl, alignItems: "center" },
  stage: { width: "100%", maxWidth: 680, gap: space.xl },
  centered: { flex: 1, alignItems: "center", justifyContent: "center", gap: space.md, padding: space.xl },
  loading: { paddingVertical: space.xxl, alignItems: "center" },
  backLink: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.honeyDeep },

  resHead: { flexDirection: "row", alignItems: "center", justifyContent: "space-between" },
  lead: { fontFamily: fonts.bodyMedium, fontSize: 16, lineHeight: 22, color: colors.inkSoft },
  heroBig: {
    fontFamily: fonts.displayHeavy,
    fontSize: 56,
    letterSpacing: -2,
    lineHeight: 58,
    marginTop: space.xs,
    ...tabular,
  },

  split: { flexDirection: "row", gap: space.md, marginTop: space.lg },
  cell: { flex: 1, backgroundColor: colors.sand, borderRadius: radius.md, padding: space.lg },
  cellKey: {
    fontFamily: fonts.mono,
    fontSize: 10.5,
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: colors.muted,
  },
  cellVal: { fontFamily: fonts.displayHeavy, fontSize: 22, color: colors.ink, marginTop: space.sm },

  section: { gap: space.md },
  sectionTitle: { fontFamily: fonts.display, fontSize: 20, letterSpacing: -0.4, color: colors.ink },

  whyRow: { flexDirection: "row", gap: space.md, alignItems: "flex-start" },
  whyIcon: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: colors.honeyTint,
    alignItems: "center",
    justifyContent: "center",
    marginTop: 1,
  },
  whyIconText: { fontFamily: fonts.displayHeavy, fontSize: 13, color: colors.honeyDeep },
  whyText: { flex: 1, fontFamily: fonts.body, fontSize: 14.5, lineHeight: 22, color: colors.inkSoft },

  whatIfIncome: { fontFamily: fonts.displayHeavy, fontSize: 30, color: colors.ink, ...tabular },
  whatIfGrid: { flexDirection: "row", gap: space.md },
  whatIfStat: {
    flex: 1,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.md,
    backgroundColor: colors.card,
    gap: space.xs,
  },
  whatIfStatValue: { fontFamily: fonts.displayHeavy, fontSize: 18, color: colors.ink },
  whatIfBand: { fontFamily: fonts.bodySemibold, fontSize: 13, color: colors.inkSoft },

  sourceLink: {
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.md,
    backgroundColor: colors.card,
  },
  sourceLinkText: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.oceanDeep },
  verified: { fontFamily: fonts.body, fontSize: 12, color: colors.muted },

  crossSell: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: space.md,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.lg,
    backgroundColor: colors.card,
  },
  crossSellText: { flex: 1, fontFamily: fonts.bodyMedium, fontSize: 14, color: colors.ink },
  pill: { backgroundColor: colors.honey, borderRadius: radius.sm, paddingHorizontal: space.sm, paddingVertical: space.xs },
  pillText: { fontFamily: fonts.bodyBold, fontSize: 11, color: colors.ink },

  cardTitle: { fontFamily: fonts.display, fontSize: 18, color: colors.ink },
  input: {
    borderWidth: 1.5,
    borderColor: colors.lineStrong,
    borderRadius: radius.md,
    paddingVertical: 14,
    paddingHorizontal: space.lg,
    fontFamily: fonts.bodyMedium,
    fontSize: 16,
    color: colors.ink,
    backgroundColor: colors.card,
    ...webNoOutline,
  },

  errorTitle: { fontFamily: fonts.display, fontSize: 18, color: colors.danger },
  muted: { fontFamily: fonts.body, fontSize: 14, lineHeight: 20, color: colors.inkSoft },
  mono: { fontFamily: fonts.mono },
});
